import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Checkbox, FormControlLabel, Grid } from '@material-ui/core'
import React, { useContext, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { REGEX } from '@flatsy/core/Form/ReactFormHooks/Field/Constant'
import FlatsyTextField from '@flatsy/core/Form/ReactFormHooks/Field/Text'
import Separator from '@flatsy/core/Layout/Separator'
import { LabelSeparator } from '@flatsy/core/MaterialUi/Typo'
import { isPublicAssetFromLamyNetwork } from '@flatsy/utils/Functions/Assets'
import i18n from '@flatsy/utils/translations'
import { FormContext, ReturnContextProps } from 'src/containers/Qualification/Context'
import LamyPrivacyNotice from 'src/utils/Lamy'

import { useFormStyles } from '../Style'
import { SharedProps } from '../Type'
import { MainApplicantData } from './Type'

const MainApplicant: React.FC<SharedProps> = ({ renderMobileFormButton }) => {
  const formClasses = useFormStyles()
  const {
    wizardContext: {
      context: { application, asset },
    },
    saveApplicationAndContinue,
  } = useContext(FormContext) as ReturnContextProps

  const errorTxtFirstName = `${i18n.t('first_name')} ${i18n
    .t('is_a_required_field')
    .toLowerCase()} `
  const errorTxtLastName = `${i18n.t('last_name')} ${i18n.t('is_a_required_field').toLowerCase()} `

  const isLamy = isPublicAssetFromLamyNetwork(asset)

  const schema = yup.object({
    first_name: yup.string().required(errorTxtFirstName),
    last_name: yup.string().required(errorTxtLastName),
    phone_number: yup
      .string()
      .matches(
        REGEX.phone,
        i18n.t('the_phone_number_entered_does_not_match_the_correct_formatting')
      )
      .required(),
    email: yup
      .string()
      .email(i18n.t('the_email_address_entered_does_not_match_the_correct_formatting'))
      .matches(
        REGEX.email,
        i18n.t('the_email_address_entered_does_not_match_the_correct_formatting')
      )
      .required(),
    consentDataCollection: yup
      .boolean()
      .oneOf([true], 'Vous devez accepter les termes pour continuer')
      .required(),
  })

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitSuccessful, isValid, isDirty },
  } = useForm<MainApplicantData>({
    mode: 'all',
    resolver: yupResolver(schema),
  })

  useEffect(() => {
    if (application) {
      reset({
        first_name: '',
        last_name: '',
        phone_number: '',
        email: '',
        consentDataCollection: false,
      })
    }
  }, [application])

  if (!application) {
    return null
  }

  const onSubmit = async (values: MainApplicantData): Promise<undefined> => {
    const { consentDataCollection, ...payload } = values
    saveApplicationAndContinue({
      lead: payload,
    })
    return undefined
  }

  return (
    <div className={formClasses.container}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <LabelSeparator align="center">
          {i18n.t('tell_us_a_bit_more_about_yourself')}
        </LabelSeparator>
        <Grid container spacing={1}>
          <Grid sm={6} xs={12} item>
            <Controller
              name="first_name"
              control={control}
              render={({ field, fieldState }) => (
                <FlatsyTextField
                  label={i18n.t('first_name')}
                  type="text"
                  {...field}
                  {...fieldState}
                  isSubmitSuccessful={isSubmitSuccessful}
                />
              )}
            />
          </Grid>
          <Grid sm={6} xs={12} item>
            <Controller
              name="last_name"
              control={control}
              render={({ field, fieldState }) => (
                <FlatsyTextField
                  label={i18n.t('last_name')}
                  type="text"
                  {...field}
                  {...fieldState}
                  isSubmitSuccessful={isSubmitSuccessful}
                />
              )}
            />
          </Grid>
        </Grid>
        <div className={formClasses.borderSeparator} />
        <LabelSeparator align="center" component="div">
          <p>{i18n.t('this_person_will_be_our_contact')}</p>
          <p>{i18n.t('give_us_the_means_to_contact_you_again_for_the_tour')}</p>
        </LabelSeparator>
        <Controller
          name="phone_number"
          control={control}
          render={({ field, fieldState }) => (
            <FlatsyTextField
              label={i18n.t('phone')}
              type="text"
              {...field}
              {...fieldState}
              isSubmitSuccessful={isSubmitSuccessful}
            />
          )}
        />
        <Separator size="24px" />
        <Controller
          name="email"
          control={control}
          render={({ field, fieldState }) => (
            <FlatsyTextField
              label={i18n.t('email_address')}
              type="text"
              {...field}
              {...fieldState}
              isSubmitSuccessful={isSubmitSuccessful}
            />
          )}
        />
        <Separator size="24px" />
        <Controller
          name="consentDataCollection"
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <FormControlLabel
              control={<Checkbox color="primary" {...field} checked={field.value} />}
              label={
                <>
                  J’accepte que les données à caractère personnel soient collectées par le
                  responsable de traitement dans le cadre du dépôt d’une candidature à la location
                  d’un bien, de l’organisation d’une visite et de la relation commerciale qui pourra
                  en découler.{' '}
                  {isLamy && <LamyPrivacyNotice trigger="Cliquer pour plus de détails" />}
                </>
              }
              labelPlacement="end"
            />
          )}
        />

        {!renderMobileFormButton ? (
          <Button
            variant="contained"
            color="primary"
            disableElevation
            type="submit"
            className={formClasses.submit}
            disabled={!isValid}
          >
            {i18n.t('continue')}
          </Button>
        ) : (
          renderMobileFormButton(isDirty, !isValid)
        )}
      </form>
    </div>
  )
}

export default MainApplicant
